// src/services/queries/userProfileQueries.ts

import type { QueryFunction, QueryKey } from "@tanstack/react-query";
import { API_BASE_URL } from "@/services";
import { userProfileQueryKeys } from "@/constants/queryKeys";
import type {
  UserProfileResponse,
  UpdateUserProfileRequest,
  UpdateUserPreferencesRequest,
  UpdateEmailRequest,
  SendVerificationEmailResponse,
  UserProfileSuccessResponse,
} from "@/types/userProfile.types";

// ============================================================================
// LOGGING UTILITIES
// ============================================================================

const logInfo = (message: string, data?: unknown) => {
  console.log(`[UserProfile] ℹ️ ${message}`, data || "");
};

const logSuccess = (message: string, data?: unknown) => {
  console.log(`[UserProfile] ✅ ${message}`, data || "");
};

const logError = (message: string, error?: unknown) => {
  console.error(`[UserProfile] ❌ ${message}`, error || "");
};

const logRequest = (method: string, url: string, data?: unknown) => {
  console.log(`[UserProfile] 🚀 ${method} ${url}`);
  if (data) {
    console.log(`[UserProfile] 📤 Request body:`, data);
  }
};

const logResponse = (method: string, status: number, data?: unknown) => {
  console.log(`[UserProfile] 📥 Response ${status} for ${method}`);
  if (data) {
    console.log(`[UserProfile] Response data:`, data);
  }
};

// ============================================================================
// ERROR EXTRACTION HELPER
// ============================================================================

/**
 * Extracts the most informative error message from a failed fetch Response
 */
const extractErrorMessage = async (
  response: Response,
  fallback: string
): Promise<string> => {
  try {
    const cloned = response.clone();
    // Prefer JSON when possible
    try {
      const data = await cloned.json();
      logInfo("Error response data:", data);
      const detail = (data && (data.detail || data.message)) as unknown;
      if (typeof detail === "string" && detail.trim().length > 0) return detail;
    } catch {
      // Not JSON; fall through to text
    }
    const text = await response.text();
    if (text && text.trim().length > 0) {
      // Sometimes servers send JSON with wrong content-type
      try {
        const parsed = JSON.parse(text);
        const detail = (parsed && (parsed.detail || parsed.message)) as unknown;
        if (typeof detail === "string" && detail.trim().length > 0)
          return detail;
      } catch {
        // Plain text body
      }
      return text.trim();
    }
  } catch {
    // ignore parsing errors
  }
  return fallback;
};

// ============================================================================
// QUERY FUNCTIONS
// ============================================================================

/**
 * Fetch user profile from backend
 * Backend will call Auth0 Management API to get user data + metadata
 */
export const fetchUserProfile: QueryFunction<
  UserProfileResponse,
  QueryKey
> = async ({ signal }: { signal?: AbortSignal }) => {
  const url = new URL(`${API_BASE_URL}/users/profile`);

  logRequest("GET", url.toString());
  logInfo("API_BASE_URL:", API_BASE_URL);

  const response = await fetch(url.toString(), {
    ...(signal && { signal }),
    headers: {
      "Content-Type": "application/json",
    },
  });

  logResponse("GET /users/profile", response.status);

  if (!response.ok) {
    const message = await extractErrorMessage(
      response,
      `Failed to fetch user profile: ${response.status}`
    );
    logError("Failed to fetch profile", message);
    throw new Error(message);
  }

  const data = await response.json();
  logSuccess("Profile fetched successfully", data);
  return data as Promise<UserProfileResponse>;
};

/**
 * Fetch user profile from backend (standalone function)
 * This bypasses the ID token and fetches fresh data from Auth0 Management API via backend
 */
export const fetchUserProfileFromBackend = async (authToken: string) => {
  const url = new URL(`${API_BASE_URL}/users/profile`);

  logRequest("GET", url.toString());
  logInfo("Fetching fresh user profile from backend...");

  const response = await fetch(url.toString(), {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const message = await extractErrorMessage(
      response,
      `Failed to fetch user profile: ${response.status}`
    );
    logError("Failed to fetch profile", message);
    throw new Error(message);
  }

  const data = await response.json();
  logSuccess("Fresh user profile fetched from backend!", data);

  // Backend can return either { user: {...} } or the user object directly
  // Handle both formats
  const userData = data.user || data;
  logInfo("Extracted user data:", userData);

  return userData;
};

/**
 * User profile query options with auth token
 * Follows the same pattern as portfolioQueries
 */
export const userProfileQueryOptions = (authToken?: string) => ({
  queryKey: userProfileQueryKeys.profile(),
  queryFn: (async ({ signal }: { signal?: AbortSignal }) => {
    const url = new URL(`${API_BASE_URL}/users/profile`);

    logRequest("GET", url.toString());
    logInfo("API_BASE_URL:", API_BASE_URL);
    logInfo("Auth token present:", !!authToken);
    if (authToken) {
      logInfo("Auth token (first 20 chars):", authToken.substring(0, 20) + "...");
    }

    const response = await fetch(url.toString(), {
      ...(signal && { signal }),
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
    });

    logResponse("GET /users/profile", response.status);

    if (!response.ok) {
      const message = await extractErrorMessage(
        response,
        `Failed to fetch user profile: ${response.status}`
      );
      logError("Failed to fetch profile", message);
      throw new Error(message);
    }

    const data = await response.json();
    logSuccess("Profile fetched successfully", data);
    return data as Promise<UserProfileResponse>;
  }) as QueryFunction<UserProfileResponse, QueryKey>,
  enabled: Boolean(authToken),
  // Cache for 5 minutes since profile doesn't change often
  staleTime: 5 * 60 * 1000,
});

// ============================================================================
// MUTATION FUNCTIONS (SIMPLIFIED - NO REACT QUERY)
// ============================================================================

/**
 * Update user profile (name, username, firstName, lastName, phoneNumber)
 * SIMPLIFIED VERSION WITH EXTENSIVE LOGGING
 */
export const updateUserProfile = async (
  data: UpdateUserProfileRequest,
  authToken?: string
): Promise<UserProfileSuccessResponse> => {
  logInfo("=".repeat(60));
  logInfo("🔄 STARTING updateUserProfile");
  logInfo("=".repeat(60));

  logInfo("Update data:", data);
  logInfo("Auth token present:", !!authToken);
  logInfo("API_BASE_URL:", API_BASE_URL);

  if (!authToken) {
    logError("No auth token provided!");
    throw new Error("Authentication required");
  }

  const url = `${API_BASE_URL}/users/profile`;
  logInfo("Full URL:", url);

  logRequest("PATCH", url, data);

  try {
    logInfo("About to call fetch...");
    logInfo("Creating AbortController for 10 second timeout...");

    // Add timeout to prevent infinite hanging
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      logError("⏱️ FETCH TIMEOUT! Request took longer than 10 seconds");
      controller.abort();
    }, 10000); // 10 second timeout

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(data),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      logInfo("Fetch completed!");
      logResponse("PATCH /users/profile", response.status);

      logInfo("Response headers:");
      response.headers.forEach((value, key) => {
        console.log(`  ${key}: ${value}`);
      });

      if (!response.ok) {
        logError(`Request failed with status ${response.status}`);
        const message = await extractErrorMessage(
          response,
          `Failed to update user profile: ${response.status}`
        );
        logError("Error message:", message);
        throw new Error(message);
      }

      logInfo("Parsing response JSON...");
      const responseData = await response.json();
      logSuccess("Profile updated successfully!", responseData);

      logInfo("=".repeat(60));
      logInfo("✅ COMPLETED updateUserProfile");
      logInfo("=".repeat(60));

      return responseData as UserProfileSuccessResponse;
    } catch (fetchError) {
      clearTimeout(timeoutId);

      if (fetchError instanceof Error && fetchError.name === 'AbortError') {
        logError("❌ REQUEST TIMED OUT after 10 seconds!");
        logError("This usually means:");
        logError("  1. Backend is not running");
        logError("  2. Backend is not accessible at the URL");
        logError("  3. CORS is blocking the request");
        logError("  4. Network connectivity issue");
        throw new Error("Request timed out - backend may not be running or accessible");
      }
      throw fetchError;
    }
  } catch (error) {
    logError("Exception caught in updateUserProfile:", error);
    if (error instanceof Error) {
      logError("Error name:", error.name);
      logError("Error message:", error.message);
      logError("Error stack:", error.stack);

      // Check for common network errors
      if (error.message.includes("Failed to fetch")) {
        logError("🚨 NETWORK ERROR DETECTED!");
        logError("Possible causes:");
        logError("  1. Backend not running at", url);
        logError("  2. CORS not configured on backend");
        logError("  3. Firewall blocking the request");
        logError("  4. Wrong URL/port");
        logError("");
        logError("💡 To debug:");
        logError("  1. Check if backend is running: curl", url);
        logError("  2. Check browser Network tab for CORS errors");
        logError("  3. Verify API_BASE_URL is correct");
      }
    }
    throw error;
  }
};

/**
 * Update user preferences (theme, notifications, emailUpdates)
 * SIMPLIFIED VERSION WITH EXTENSIVE LOGGING
 */
export const updateUserPreferences = async (
  data: UpdateUserPreferencesRequest,
  authToken?: string
): Promise<UserProfileSuccessResponse> => {
  logInfo("=".repeat(60));
  logInfo("🔄 STARTING updateUserPreferences");
  logInfo("=".repeat(60));

  logInfo("Preferences data:", data);
  logInfo("Auth token present:", !!authToken);
  logInfo("API_BASE_URL:", API_BASE_URL);

  if (!authToken) {
    logError("No auth token provided!");
    throw new Error("Authentication required");
  }

  const url = `${API_BASE_URL}/users/preferences`;
  logInfo("Full URL:", url);

  logRequest("PATCH", url, data);

  try {
    logInfo("About to call fetch...");

    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(data),
    });

    logInfo("Fetch completed!");
    logResponse("PATCH /users/preferences", response.status);

    if (!response.ok) {
      logError(`Request failed with status ${response.status}`);
      const message = await extractErrorMessage(
        response,
        `Failed to update user preferences: ${response.status}`
      );
      logError("Error message:", message);
      throw new Error(message);
    }

    logInfo("Parsing response JSON...");
    const responseData = await response.json();
    logSuccess("Preferences updated successfully!", responseData);

    logInfo("=".repeat(60));
    logInfo("✅ COMPLETED updateUserPreferences");
    logInfo("=".repeat(60));

    return responseData as UserProfileSuccessResponse;
  } catch (error) {
    logError("Exception caught in updateUserPreferences:", error);
    if (error instanceof Error) {
      logError("Error message:", error.message);
      logError("Error stack:", error.stack);
    }
    throw error;
  }
};

/**
 * Update user email
 * SIMPLIFIED VERSION WITH EXTENSIVE LOGGING
 */
export const updateUserEmail = async (
  data: UpdateEmailRequest,
  authToken?: string
): Promise<UserProfileSuccessResponse> => {
  logInfo("=".repeat(60));
  logInfo("🔄 STARTING updateUserEmail");
  logInfo("=".repeat(60));

  logInfo("Email data:", data);
  logInfo("Auth token present:", !!authToken);
  logInfo("API_BASE_URL:", API_BASE_URL);

  if (!authToken) {
    logError("No auth token provided!");
    throw new Error("Authentication required");
  }

  const url = `${API_BASE_URL}/users/email`;
  logInfo("Full URL:", url);

  logRequest("PATCH", url, data);

  try {
    logInfo("About to call fetch...");

    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(data),
    });

    logInfo("Fetch completed!");
    logResponse("PATCH /users/email", response.status);

    if (!response.ok) {
      logError(`Request failed with status ${response.status}`);
      const message = await extractErrorMessage(
        response,
        `Failed to update email: ${response.status}`
      );
      logError("Error message:", message);
      throw new Error(message);
    }

    logInfo("Parsing response JSON...");
    const responseData = await response.json();
    logSuccess("Email updated successfully!", responseData);

    logInfo("=".repeat(60));
    logInfo("✅ COMPLETED updateUserEmail");
    logInfo("=".repeat(60));

    return responseData as UserProfileSuccessResponse;
  } catch (error) {
    logError("Exception caught in updateUserEmail:", error);
    if (error instanceof Error) {
      logError("Error message:", error.message);
      logError("Error stack:", error.stack);
    }
    throw error;
  }
};

/**
 * Send verification email
 * SIMPLIFIED VERSION WITH EXTENSIVE LOGGING
 */
export const sendVerificationEmail = async (
  authToken?: string
): Promise<SendVerificationEmailResponse> => {
  logInfo("=".repeat(60));
  logInfo("🔄 STARTING sendVerificationEmail");
  logInfo("=".repeat(60));

  logInfo("Auth token present:", !!authToken);
  logInfo("API_BASE_URL:", API_BASE_URL);

  if (!authToken) {
    logError("No auth token provided!");
    throw new Error("Authentication required");
  }

  const url = `${API_BASE_URL}/users/send-verification`;
  logInfo("Full URL:", url);

  logRequest("POST", url);

  try {
    logInfo("About to call fetch...");

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
    });

    logInfo("Fetch completed!");
    logResponse("POST /users/send-verification", response.status);

    if (!response.ok) {
      logError(`Request failed with status ${response.status}`);
      const message = await extractErrorMessage(
        response,
        `Failed to send verification email: ${response.status}`
      );
      logError("Error message:", message);
      throw new Error(message);
    }

    logInfo("Parsing response JSON...");
    const responseData = await response.json();
    logSuccess("Verification email sent successfully!", responseData);

    logInfo("=".repeat(60));
    logInfo("✅ COMPLETED sendVerificationEmail");
    logInfo("=".repeat(60));

    return responseData as SendVerificationEmailResponse;
  } catch (error) {
    logError("Exception caught in sendVerificationEmail:", error);
    if (error instanceof Error) {
      logError("Error message:", error.message);
      logError("Error stack:", error.stack);
    }
    throw error;
  }
};
